import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { EmailCampaign, CampaignFilters } from '@/types/emailCampaigns'

const KEY = ['email_campaigns'] as const

export function useEmailCampaignsList() {
  return useQuery({
    queryKey: KEY,
    queryFn: async (): Promise<EmailCampaign[]> => {
      const { data, error } = await (supabase as any)
        .from('email_campaigns')
        .select('*')
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as EmailCampaign[]
    },
    refetchInterval: 20_000,
  })
}

export function useEmailCampaign(id: string | undefined) {
  return useQuery({
    queryKey: [...KEY, id],
    enabled: !!id,
    queryFn: async (): Promise<EmailCampaign | null> => {
      if (!id) return null
      const { data, error } = await (supabase as any)
        .from('email_campaigns')
        .select('*')
        .eq('id', id)
        .single()
      if (error) throw error
      return data as EmailCampaign
    },
    refetchInterval: 15_000,
  })
}

export interface CreateCampaignInput {
  name: string
  filters: CampaignFilters
  throttle_per_day: number
  send_window_start: string
  send_window_end: string
  timezone: string
  start_at?: string | null
  prospects: Array<{
    prospect_id: string
    email_to: string
    email_subject: string
    email_body: string
  }>
}

function randomToken(): string {
  const bytes = new Uint8Array(24)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

export function useCreateCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: CreateCampaignInput) => {
      const { data: campaign, error: cErr } = await (supabase as any)
        .from('email_campaigns')
        .insert({
          name: input.name,
          filters: input.filters,
          throttle_per_day: input.throttle_per_day,
          send_window_start: input.send_window_start,
          send_window_end: input.send_window_end,
          timezone: input.timezone,
          start_at: input.start_at ?? null,
          status: 'running',
          started_at: new Date().toISOString(),
        })
        .select('*')
        .single()
      if (cErr) throw cErr

      const rows = input.prospects.map((p) => ({
        campaign_id: campaign.id,
        prospect_id: p.prospect_id,
        email_to: p.email_to,
        email_subject: p.email_subject,
        email_body: p.email_body,
        unsubscribe_token: randomToken(),
      }))
      if (rows.length > 0) {
        const { error: pErr } = await (supabase as any).from('email_campaign_prospects').insert(rows)
        if (pErr) throw pErr
      }
      return campaign as EmailCampaign
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useUpdateCampaignStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      status,
    }: {
      id: string
      status: 'paused' | 'running' | 'cancelled'
    }) => {
      const patch: Record<string, unknown> = { status }
      if (status === 'cancelled') patch.completed_at = new Date().toISOString()
      if (status === 'running') patch.started_at = new Date().toISOString()
      const { error } = await (supabase as any).from('email_campaigns').update(patch).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}
